// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { View, Stepper } from 'reshaped';

import { getCart, selectCartIsReopenable, selectCartUUID } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Button, Svg } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { tryResumeCart } from 'src/modules/app/actions';

import styles from './FunnelControls.scss';

export type FunnelControlsProps = {
  /**
   * 1. Address
   * 2. Summary
   * 3. Payment
   * 4. Payment failed
   * 5. Confirmation (payment return success) – no breadcrumbs or menu displayed
   */
  currentStep?: 1 | 2 | 3 | 4 | 5,
};

export function FunnelControls({
  currentStep = 1,
}: FunnelControlsProps): React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const steps = React.useMemo(
    () => [__('Address'), __('Summary'), __('Payment')],
    [__],
  );
  const stepperStep = Math.min(currentStep, steps.length);

  // after the paiement the cart is emptied
  const cartId = useAppSelector(selectCartUUID);
  // cart is cartIsReopenable tell us is the status is closed
  const cartIsReopenable = useAppSelector(selectCartIsReopenable);

  const onClickHandler = async () => {
    // to avoid to reopen a cart that should not be
    // we check first if we have a cart via cartId
    // then if the current status make it reopenable
    // so we fetch it, that will update the status
    // and we try to resume it
    if (!!cartId && cartIsReopenable)
      await dispatch(getCart()).then(() => dispatch(tryResumeCart()));
  };

  return (
    <div className={styles.root}>
      <div className={styles.funnelControls}>
        <Button
          onClick={onClickHandler}
          to="/"
          className={styles.logoContainer}
        >
          <Svg
            // needed for the display title
            id="gandi-net-logo"
            name="gandiIcon"
            theme="neutral-strong"
            title={__('Return to homepage')}
            className={styles.logo}
          />
        </Button>
        {currentStep !== 5 && (
          <View
            maxWidth={{
              s: 4,
              m: 300,
            }}
            width="100%"
            justify="center"
            align="center"
            grow
            paddingInline={{
              s: 1,
              m: 4,
              l: 8,
            }}
          >
            <Stepper
              className={styles.stepper}
              activeId={currentStep - 1}
              labelDisplay={{ s: 'hidden', m: 'inline' }}
              direction="row"
            >
              {steps.map((title, index) => (
                <Stepper.Item
                  key={title}
                  title={title}
                  completed={index < stepperStep - 1}
                />
              ))}
            </Stepper>
          </View>
        )}
      </div>
    </div>
  );
}
